/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Notification from "../common/Notification";
import PropTypes from "prop-types";

import {
  Button,
  IconButton,
  Grid,
  MenuItem,
  Dialog,
  InputLabel,
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import CloseIcon from "@material-ui/icons/Close";

import StyledTextField from "../common/StyledTextField";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";
import types from "../../helpers/types";
import { createGoTrackUser } from "../../slices/goTrackUserSlice";
import { isAdministrator, isCarrier } from "../../helpers/goTrackUserUtils";
import AutoCompleteMultiSelect from "../common/AutoCompleteMultiSelect";
import { getUserRecordExists } from "../../api/api";
import { fetchUsers } from "../../slices/userManagementSlice";

const InviteNewUserModal = ({ handleClose, open }) => {
  const [userType, setUserType] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    userType: false,
    email: false,
    firstName: false,
    lastName: false,
    emailTaken: false,
    carrierAssociation: false,
    carrierOrg: false,
  });
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [carrierAssociationStep, setCarrierAssociationStep] = useState(false);
  const [allCarriers, setAllCarriers] = useState([]);
  const [associatedCarrierList, setAssociatedCarrierList] = useState([]);
  const [getUserExistsRequestStatus, setGetUserExistsRequestStatus] = useState(
    types.requestStatuses.initial
  );
  const [carrierAutoCompleteErrorMessage, setCarrierAutoCompleteErrorMessage] =
    useState("");

  const isCreatingUser = useSelector(
    (state) => state.goTrackUser.isCreatingUser
  );
  const { goTrackUser } = useSelector((state) => state.goTrackUser);
  const { carriers } = useSelector((state) => state.carriers);

  const { currentPage } = useSelector((state) => state.userManagementFilters);

  const dispatch = useDispatch();

  const handleUserTypeChange = (event) => {
    setUserType(event.target.value);
    setValidationErrors({ ...validationErrors, userType: false });
  };

  const handleUserEmailChange = (event) => {
    setEmail(event.target.value);
    setValidationErrors({ ...validationErrors, email: false });
  };

  const handleUserFirstNameChange = (event) => {
    setFirstName(event.target.value);
    setValidationErrors({ ...validationErrors, firstName: false });
  };

  const handleUserLastNameChange = (event) => {
    setLastName(event.target.value);
    setValidationErrors({ ...validationErrors, lastName: false });
  };

  const handleUserCreationSuccess = () => {
    handleClose();
    setShowSuccessNotification(true);
    dispatch(fetchUsers(currentPage));
  };

  const handleUserCreation = async () => {
    const isValid = await isNewUserFormValid();
    const carrierIds = associatedCarrierList.map((carrier) => carrier.id);

    if (isValid) {
      dispatch(
        createGoTrackUser(
          {
            email,
            firstName,
            lastName,
            organizationsId: isCarrier([userType])
              ? associatedCarrierList[0].organizationsId
              : 1,
            role: userType,
            phone: "",
          },
          handleUserCreationSuccess,
          carrierIds
        )
      );
    }
  };

  const isNewUserFormValid = async () => {
    const updatedValidationErrors = { ...validationErrors };

    // https://www.w3resource.com/javascript/form/email-validation.php
    // personal info part of email contains only: A-Z, a-z, 0-9, !#$%&'*+-/=?^_`{|}~, . (provided that is is not the first or last character and it will not come one right after the other)
    // domain part contains letters, digits, hyphens and dots
    const validEmail =
      email &&
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        email
      );

    if (!userType) {
      updatedValidationErrors.userType = true;
    }

    if (!email.trim().length) {
      updatedValidationErrors.email = true;
    }

    if (email.trim().length) {
      setGetUserExistsRequestStatus(types.requestStatuses.inProgress);
      try {
        const userExists = await getUserRecordExists(email.trim());
        setGetUserExistsRequestStatus(types.requestStatuses.success);
        if (userExists) {
          updatedValidationErrors.emailTaken = true;
        }
      } catch {
        setGetUserExistsRequestStatus(types.requestStatuses.failure);
      }
    }

    if (!firstName.trim().length) {
      updatedValidationErrors.firstName = true;
    }

    if (!lastName.trim().length) {
      updatedValidationErrors.lastName = true;
    }

    if (!validEmail) {
      updatedValidationErrors.email = true;
    }

    if (
      userType === types.goTrackUserRoles.carrier &&
      carrierAssociationStep &&
      associatedCarrierList.length === 0
    ) {
      setCarrierAutoCompleteErrorMessage(
        "At least one carrier is required for this user type."
      );
      updatedValidationErrors.carrierAssociation = true;
    }

    setValidationErrors({ ...validationErrors, ...updatedValidationErrors });
    const isValid = Object.values(updatedValidationErrors).every(
      (value) => !value
    );
    return isValid;
  };

  const setCarrierAssociationValidation = (isValid) => {
    validationErrors.carrierAssociation = isValid;
  };

  const handleNextStep = async () => {
    const isValid = await isNewUserFormValid();
    if (isValid) {
      setCarrierAssociationStep(true);
    }
  };

  useEffect(() => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setUserType("");
    setAssociatedCarrierList([]);
    setCarrierAssociationStep(false);
    setValidationErrors({
      userType: false,
      email: false,
      firstName: false,
      lastName: false,
      emailTaken: false,
      carrierOrg: false,
    });
  }, [open]);

  useEffect(() => {
    setAllCarriers(
      carriers.map((carrier) => ({
        id: carrier.id,
        name: carrier.carrierName,
        organizationsId: carrier.organizationsId,
      }))
    );
  }, [carriers]);

  useEffect(() => {
    if (
      carrierAssociationStep &&
      associatedCarrierList.length > 0 &&
      !associatedCarrierList[0].organizationsId
    ) {
      setCarrierAutoCompleteErrorMessage(
        `Carrier ${associatedCarrierList[0].name} is missing a carrier organization, please add a carrier organization from Carrier Management and try again.`
      );
      validationErrors.carrierOrg = true;
    } else {
      validationErrors.carrierOrg = false;
      setCarrierAutoCompleteErrorMessage("");
    }
  }, [associatedCarrierList]);

  return (
    <>
      {open && (
        <Dialog
          onClose={handleClose}
          open={true}
          maxWidth="md"
          fullWidth={true}
          disableBackdropClick={true}
          data-testid="invite-new-user-modal"
        >
          <MuiDialogTitle>
            Invite New User
            {carrierAssociationStep && " (continued)"}
            <IconButton style={{ float: "right" }} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </MuiDialogTitle>
          <MuiDialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {carrierAssociationStep
                  ? "Please associate the carrier user to at least one existing carrier user (max 30):"
                  : "Please provide the following details to invite a new user. This will send an invitation email to the user with instructions on how to get started."}
              </Grid>
              {carrierAssociationStep ? (
                <Grid item xs={12}>
                  <AutoCompleteMultiSelect
                    autoCompleteTestId="invite-user-carrier-association-autocomplete"
                    options={allCarriers}
                    selectedOptions={associatedCarrierList}
                    setSelectedOptions={setAssociatedCarrierList}
                    error={
                      validationErrors.carrierAssociation ||
                      validationErrors.carrierOrg
                    }
                    setError={setCarrierAssociationValidation}
                    errorMessage={carrierAutoCompleteErrorMessage}
                    label="Associate a carrier (required)"
                    autoCompleteStyles={{
                      width: "400px",
                      display: "inline-block",
                      position: "relative",
                      marginRight: "8px",
                      lineHeight: "3.5",
                    }}
                    maxNumberOfSelectedOptions={30}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={3}>
                    <StyledFormControl
                      variant="outlined"
                      fullWidth={true}
                      error={validationErrors.userType}
                    >
                      <InputLabel id="user-type">User type</InputLabel>
                      <StyledSelect
                        value={userType}
                        label="User type"
                        labelId="user-type"
                        onChange={handleUserTypeChange}
                        data-testid="user-type-select"
                        inputProps={{
                          "data-testid": "user-type-select-input",
                        }}
                      >
                        {isAdministrator(goTrackUser.roles) && (
                          <MenuItem
                            value={types.goTrackUserRoles.administrator}
                          >
                            Admin
                          </MenuItem>
                        )}
                        (
                        <MenuItem value={types.goTrackUserRoles.carrier}>
                          Carrier
                        </MenuItem>
                        )
                        <MenuItem value={types.goTrackUserRoles.shipper}>
                          Shipper
                        </MenuItem>
                        <MenuItem value={types.goTrackUserRoles.shipperManager}>
                          Shipper Manager
                        </MenuItem>
                      </StyledSelect>
                    </StyledFormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <StyledTextField
                      onChange={handleUserEmailChange}
                      label="Email"
                      variant="outlined"
                      value={email}
                      fullWidth={true}
                      error={
                        validationErrors.email || validationErrors.emailTaken
                      }
                      helperText={
                        validationErrors.emailTaken
                          ? "This user already exists in RouteConnect™."
                          : ""
                      }
                      inputProps={{
                        "data-testid": "new-user-email-input",
                        maxLength: 79,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <StyledTextField
                      onChange={handleUserFirstNameChange}
                      label="First name"
                      variant="outlined"
                      value={firstName}
                      fullWidth={true}
                      error={validationErrors.firstName}
                      inputProps={{
                        "data-testid": "new-user-first-name-input",
                        maxLength: 128,
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <StyledTextField
                      onChange={handleUserLastNameChange}
                      label="Last name"
                      variant="outlined"
                      value={lastName}
                      fullWidth={true}
                      error={validationErrors.lastName}
                      inputProps={{
                        "data-testid": "new-user-last-name-input",
                        maxLength: 128,
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </MuiDialogContent>
          <MuiDialogActions>
            {userType && isCarrier([userType]) && !carrierAssociationStep ? (
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={
                  getUserExistsRequestStatus ===
                  types.requestStatuses.inProgress
                }
                data-testid="user-invite-modal-continue-button"
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleUserCreation}
                disabled={
                  isCreatingUser ||
                  getUserExistsRequestStatus ===
                    types.requestStatuses.inProgress
                }
                data-testid="send-user-invite-button"
              >
                Send Invitation
              </Button>
            )}
          </MuiDialogActions>
        </Dialog>
      )}
      <Notification
        message="New user invited."
        open={showSuccessNotification}
        setOpen={setShowSuccessNotification}
      />
    </>
  );
};

InviteNewUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default InviteNewUserModal;
